import { useState, MouseEvent, useEffect, CSSProperties } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Link,
  IconButton,
  AppBar,
  Toolbar,
  Box,
  useMediaQuery,
  Typography,
  Backdrop,
  Stack,
} from '@mui/material';

import {
  State,
  HeaderDialogsEnum,
  UserKindEnum,
  UserTariffPlanEnum,
  NotificationTypeEnum,
} from '@/types';
import { stringAvatar } from '@/utils';
import { LogIn, SignUp, DialogTariffPlan, MyButton } from '@/components';

import {
  StyledLogo,
  StyledButtonsAvatar,
  StyledButton,
  StyledButtonSecondary,
  StyledIconButton,
  StyledButtonOutlined,
} from './stylesHeader';
import AddAd from './addAd.svg';
import Dollar from './dollar.svg';
import Messenger from './messenger.svg';
import AddRequest from './addRequest.svg';
import AddTelegram from './telegram.svg';
import { useDialogs } from '@/components/Dashboard/DialogsContext';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AccountMenu } from '@/components/Sidebar/AccountMenu';
import { HeaderMenu } from '@/components/Sidebar/HeaderMenu';
import { usersProvider } from '@/providers';
import { appSetNotification } from '@/store';
import { CustomBackdrop } from '../Dashboard/CustomBackdrop';
import { shouldRestrictByTariff, shouldShowBillingUi } from '@/utils/tariffPolicy';

export const Header = ({
  count,
  setCount,
  openHeaderBackdrop,
  setOpenHeaderBackdrop,
  contentMaxWidth,
}: {
  count?: number;
  setCount?: (value: number) => void;
  openHeaderBackdrop?: boolean;
  setOpenHeaderBackdrop?: (value: boolean) => void;
  contentMaxWidth?: string | number;
}) => {
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const { openDialog, setOpenDialog } = useDialogs();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { isLoggedIn } = useSelector(({ auth }: State) => auth);
  const user = useSelector(({ auth }: State) => auth.user);
  const isAbove900 = useMediaQuery('(min-width:1200px)');
  const isAccountMenuOpen = Boolean(anchorEl);

  const isDisabledCreatingAd =
    user.kind === UserKindEnum.Foreign ||
    (user.kind === UserKindEnum.Broker && user.employer === null) ||
    (user.kind === UserKindEnum.Manager && user.employer === null);

  const isForeign = user.kind === UserKindEnum.Foreign;
  const isManager = user.kind === UserKindEnum.Manager;
  const isBroker = user.kind === UserKindEnum.Broker;
  const isAgency = user.kind === UserKindEnum.Agency;
  const isDeveloper = user.kind === UserKindEnum.Developer;
  const isForeignPermission = isForeign && user.verified;
  const isManagerPermission = isManager && user.employer !== null;
  const isBrokerPermission = isBroker && user.employer !== null;
  const isClientRequestVisible =
    isBrokerPermission || isManagerPermission || isForeignPermission || isDeveloper || isAgency;

  const isDisabledCreatingRequest =
    user.kind === UserKindEnum.Foreign ||
    (user.kind === UserKindEnum.Broker && user.employer === null) ||
    (user.kind === UserKindEnum.Manager && user.employer === null);

  const isTariffFree = shouldRestrictByTariff(user);
  const isTariffTrial = user.tariffPlan?.name === UserTariffPlanEnum.Trial;
  const isTariffVisible = shouldShowBillingUi() && (isTariffFree || isTariffTrial);

  const isVisibleUpgrade =
    (user.kind === UserKindEnum.Broker && user.employer === null) ||
    (user.kind === UserKindEnum.Manager && user.employer === null);

  const isAdmin = user.kind === UserKindEnum.Admin;
  const isVisible = !user || isDisabledCreatingAd;

  useEffect(() => {
    if (isAbove900) {
      setIsSidebarOpen(false);
    }
  }, [isAbove900]);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const handleClickOpenDialog = (dialogType: HeaderDialogsEnum) => {
    setOpenDialog((prevState) => ({
      ...prevState,
      [dialogType]: !prevState[dialogType],
    }));
  };

  const hasSubscriptions = user.hasSubscriptions || false;
  const isTrial = user.statusSubscription === 'trialing';

  const calculateDaysRemaining = (trialEndDate: string): number => {
    const currentDate = new Date();
    const trialEnd = new Date(trialEndDate);
    const timeDifference = trialEnd.getTime() - currentDate.getTime();
    return Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
  };
  const trialPeriod = user?.trialEnd && calculateDaysRemaining(user?.trialEnd);

  const periodValue = isTariffFree
    ? hasSubscriptions
      ? 'Upgrade'
      : 'Get trial'
    : isTrial && trialPeriod
      ? `Trial: ${trialPeriod} days left`
      : 'Active';

  if (isLoggedIn === null) return null;
  return (
    <header
      data-qa='header'
      style={{
        height: '72px',
        boxShadow: '0px 1px 1px 0px #00000026',
        fontSize: '16px',
        position: 'relative',
        width: '100%',
        zIndex: 1200,
      }}
    >
      <AppBar
        component='nav'
        position='static'
        color='default'
        elevation={0}
        sx={{
          borderRadius: 0,
          backgroundColor: '#FFF',
          width: '100%',
        }}
      >
        <Box
          data-qa='header-content'
          sx={{
            boxSizing: contentMaxWidth === undefined ? undefined : 'border-box',
            marginInline: contentMaxWidth === undefined ? undefined : { lg: 'auto' },
            maxWidth: contentMaxWidth === undefined ? undefined : { lg: contentMaxWidth },
            padding:
              contentMaxWidth === undefined
                ? { xs: '0 16px', md: '0 16px 0 32px' }
                : { xs: '0 16px', md: '0 16px 0 32px', lg: '0 24px 0 16px' },
            width: '100%',
          }}
        >
          <Toolbar
            sx={{ display: 'flex', height: '72px', minHeight: '72px !important', padding: 0 }}
          >
            <Link href='/'>
              <StyledLogo src={'/Img/logo.svg'} />
            </Link>
            <Box sx={{ marginLeft: 'auto' }}>
              <StyledButtonsAvatar>
                {isLoggedIn && (
                  <>
                    {isAbove900 && (
                      <>
                        {count !== undefined && count < 4 && setCount && (
                          <CustomBackdrop
                            openHeaderBackdrop={openHeaderBackdrop}
                            setOpenHeaderBackdrop={setOpenHeaderBackdrop}
                            count={count}
                            setCount={setCount}
                          />
                        )}
                        {!isAdmin && (
                          <SupportButton
                            style={{
                              maxWidth: '173px',
                              width: 'auto',
                              zIndex: count === 0 ? 1600 : 1,
                            }}
                          />
                        )}
                        <UpgradeButton
                          style={{ maxWidth: '173px', width: 'auto' }}
                          isVisible={!user || isVisibleUpgrade || !isTariffVisible}
                          isAdmin={isAdmin}
                          handleClickOpenDialog={handleClickOpenDialog}
                          hasSubscriptions={hasSubscriptions}
                        />
                        {!isAdmin && (
                          <TelegramBotButton
                            style={{
                              maxWidth: '220px',
                              width: 'auto',
                              zIndex: count === 1 ? 1600 : 1,
                            }}
                            isTariffFree={isTariffFree}
                            handleClickOpenDialog={handleClickOpenDialog}
                          />
                        )}
                        <AddListingButton
                          style={{
                            maxWidth: '173px',
                            width: 'auto',
                            zIndex: count === 2 ? 1600 : 1,
                          }}
                          isVisible={isVisible}
                          isAdmin={isAdmin}
                          handleClickOpenDialog={handleClickOpenDialog}
                          count={count}
                        />
                        <ClientRequestButton
                          style={{
                            maxWidth: '220px',
                            width: 'auto',
                            zIndex: count === 3 ? 1600 : 1,
                          }}
                          isVisible={isClientRequestVisible}
                          isTariffFree={isTariffFree}
                          handleClickOpenDialog={handleClickOpenDialog}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            gap: '8px',
                            cursor: 'pointer',
                            alignItems: 'center',
                          }}
                          onClick={handlePopoverOpen}
                        >
                          <StyledIconButton
                            size='small'
                            aria-controls={isAccountMenuOpen ? 'account-menu' : undefined}
                            aria-haspopup='true'
                            aria-expanded={isAccountMenuOpen ? 'true' : undefined}
                          >
                            <Avatar src={user?.media?.[0]?.fileUrl} {...stringAvatar(user?.name)} />
                            <Box sx={{ marginLeft: '5px', color: '#2a3842' }}>
                              <Typography
                                sx={{ fontSize: '14px', fontWeight: 600, textAlign: 'left' }}
                              >
                                {user.tariffPlan?.name.split(' ')[0]}
                              </Typography>
                              <Typography
                                sx={{ fontSize: '14px', textAlign: 'left', opacity: 0.5 }}
                              >
                                {periodValue}
                              </Typography>
                            </Box>
                          </StyledIconButton>
                          <ExpandMoreIcon
                            sx={{
                              transform: isAccountMenuOpen ? 'rotate(180deg)' : 'none',
                            }}
                          />
                        </Box>
                        <AccountMenu
                          anchorEl={anchorEl}
                          open={isAccountMenuOpen}
                          handleClose={() => setAnchorEl(null)}
                        />
                      </>
                    )}

                    {!isAbove900 && (
                      <IconButton
                        edge='start'
                        color='inherit'
                        aria-label='open drawer'
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                      >
                        {isSidebarOpen ? <CloseIcon /> : <MenuIcon />}
                      </IconButton>
                    )}
                  </>
                )}
              </StyledButtonsAvatar>
              <SignUp handleOpenDialog={handleClickOpenDialog} openDialog={openDialog} />
              <LogIn handleOpenDialog={handleClickOpenDialog} openDialog={openDialog} />
            </Box>
          </Toolbar>
          {isSidebarOpen && (
            <HeaderMenu
              isVisible={isVisible}
              isUpgradeButtonVisible={!user || isVisibleUpgrade || !isTariffVisible}
              isAdmin={isAdmin}
              isClientRequestVisible={isClientRequestVisible}
              isTariffFree={isTariffFree}
              onClick={() => {
                setIsSidebarOpen(false);
              }}
            />
          )}
        </Box>
      </AppBar>
      <DialogTariffPlan
        open={openDialog.tariff}
        onClose={() => handleClickOpenDialog(HeaderDialogsEnum.Tariff)}
      />
    </header>
  );
};

export function SupportButton({ style }: { style?: CSSProperties }) {
  const navigate = useNavigate();
  const adminChatId =
    process.env.REACT_APP_SUPPORT_CHAT_ID || 'd3c0cd5e-c3d8-4a1e-bafe-3a08dc7ef2ea';

  return (
    <StyledButtonSecondary
      style={{ width: '100%', ...style }}
      variant='contained'
      color='secondary'
      onClick={() => navigate(`/chat/${adminChatId}`)}
    >
      <img src={Messenger} alt='Support' />
      Support
    </StyledButtonSecondary>
  );
}

export function UpgradeButton({
  style,
  isVisible,
  isAdmin,
  handleClickOpenDialog,
  hasSubscriptions,
}: {
  style?: CSSProperties;
  isVisible?: boolean;
  isAdmin?: boolean;
  hasSubscriptions: boolean;
  handleClickOpenDialog: (value: HeaderDialogsEnum) => void;
}) {
  if (!shouldShowBillingUi() || isVisible) return null;

  return (
    <StyledButton
      style={{ width: '100%', ...style }}
      type='submit'
      variant='contained'
      color='primary'
      onClick={() => handleClickOpenDialog(HeaderDialogsEnum.Tariff)}
    >
      <img src={Dollar} alt={`${hasSubscriptions} ? 'Upgrade' : 'Get trial'`} />
      {hasSubscriptions ? 'Upgrade' : 'Get trial'}
    </StyledButton>
  );
}

export function AddListingButton({
  style,
  isVisible,
  isAdmin,
  handleClickOpenDialog,
  count,
}: {
  style?: CSSProperties;
  isVisible?: boolean;
  isAdmin?: boolean;
  count?: number;
  handleClickOpenDialog: (value: HeaderDialogsEnum) => void;
}) {
  const navigate = useNavigate();
  const stepOfStudy = localStorage.getItem('study');
  if (isVisible && stepOfStudy === null) return null;

  return (
    <StyledButton
      style={{ width: '100%', ...style }}
      type='submit'
      variant='contained'
      color='primary'
      onClick={() => {
        if (isAdmin) {
          navigate('/admin/offplan/create');
        } else {
          navigate('/ads/create');
        }
      }}
    >
      <img src={AddAd} alt='Add Ad' />
      {isAdmin ? 'Add Off Plan' : 'Add listing'}
    </StyledButton>
  );
}

export function ClientRequestButton({
  style,
  isVisible,
  handleClickOpenDialog,
  isTariffFree,
}: {
  style?: CSSProperties;
  isVisible?: boolean;
  isTariffFree?: boolean;
  handleClickOpenDialog: (value: HeaderDialogsEnum) => void;
}) {
  const navigate = useNavigate();
  const stepOfStudy = localStorage.getItem('study');

  if (!isVisible && stepOfStudy === null) return null;

  return (
    <StyledButtonOutlined
      style={{ width: '100%', ...style }}
      type='submit'
      variant='outlined'
      onClick={() =>
        isTariffFree
          ? handleClickOpenDialog(HeaderDialogsEnum.Tariff)
          : navigate('/client-request/create')
      }
    >
      <img src={AddRequest} alt='Add client request' />
      Add client request
    </StyledButtonOutlined>
  );
}

export function TelegramBotButton({
  style,
  handleClickOpenDialog,
  isTariffFree,
}: {
  style?: CSSProperties;
  isTariffFree?: boolean;
  handleClickOpenDialog: (value: HeaderDialogsEnum) => void;
}) {
  const dispatch = useDispatch();

  const [url, setUrl] = useState<string>();

  const getTelegramUrl = async () => {
    const { ok, data, status, message } = await usersProvider.botConnectToken();
    if (ok && data) {
      setUrl(data);
    } else {
      dispatch(appSetNotification(NotificationTypeEnum.Error, message, status));
    }
  };

  useEffect(() => {
    getTelegramUrl();
  }, []);

  const telegramConnect = () => {
    if (url) {
      window.open(`${url}`, '_blank');
    }
  };

  return (
    <StyledButtonOutlined
      style={{ width: '100%', ...style }}
      type='submit'
      variant='outlined'
      onClick={() =>
        isTariffFree ? handleClickOpenDialog(HeaderDialogsEnum.Tariff) : telegramConnect()
      }
    >
      <img src={AddTelegram} alt='Add Telegram bot' />
      Add Telegram bot
    </StyledButtonOutlined>
  );
}
